import { useFrame } from '@react-three/fiber'
import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { useSurfaceProps } from '../util/SurfaceProvider'
import { ComponentSurfaceMeshData } from '@asset-viz/utils/helpers'
import { RenderLayerID } from '@asset-viz/utils/renderingDefines'
import React from 'react'
import * as THREE from 'three'
import { Color } from 'three'
import { ComponentSurfaceWrappingMode } from 'types'

/**
 * The base layer for asset rendering; the hoverable part of a component which
 * other layers (like heatmaps) are "overlays" on top of.
 * @constructor
 */
export const BaseLayerChunk3D: React.FC<{
  material?: THREE.Material | THREE.Material[]
}> = ({ material }) => {
  //----------------------------------------------------------------------------

  const { assetColorString, clipConfig, assetOpacity, offscreenRender } =
    useAssetVizInternal()
  const { bounds, geometry, wrapMode, componentModel } = useSurfaceProps()
  const [currentOpacity, setCurrentOpacity] = React.useState<number>(1.0)
  const importedMaterials = React.useMemo(
    () =>
      material
        ? (Array.isArray(material) ? material : [material]).map((item) =>
            item.clone()
          )
        : undefined,
    [material]
  )
  React.useEffect(
    () => () => importedMaterials?.forEach((item) => item.dispose()),
    [importedMaterials]
  )
  React.useEffect(() => {
    importedMaterials?.forEach((item, index) => {
      const original = Array.isArray(material) ? material[index] : material
      item.opacity = (original?.opacity ?? 1) * currentOpacity
      item.transparent = true
      item.clippingPlanes = clipConfig.planes
      item.polygonOffset = true
      item.polygonOffsetFactor = item.polygonOffsetUnits = 10
    })
  }, [importedMaterials, material, currentOpacity, clipConfig.planes])

  const userData = React.useMemo<ComponentSurfaceMeshData>(
    () => ({
      bounds,
      componentModel,
      doWrapX: wrapMode === ComponentSurfaceWrappingMode.ComponentWrapsX,
      doWrapY: wrapMode === ComponentSurfaceWrappingMode.ComponentWrapsY,
    }),
    [bounds, wrapMode, componentModel]
  )

  const assetColor = React.useMemo(
    () => new Color(assetColorString).convertSRGBToLinear(),
    [assetColorString]
  )

  const getSurfaceOpacity = React.useCallback(
    () =>
      typeof assetOpacity === 'number'
        ? assetOpacity
        : assetOpacity(componentModel),
    [assetOpacity, componentModel]
  )

  useFrame((_, delta) => {
    const targetOpacity = getSurfaceOpacity()
    // Smoothly interpolate to target opacity
    setCurrentOpacity((prevOpacity) =>
      THREE.MathUtils.lerp(
        prevOpacity,
        targetOpacity,
        offscreenRender ? 1 : Math.min(1, delta * 10.0) // if rendering off-screen we want this transition to be instantaneous
      )
    )
  })

  //----------------------------------------------------------------------------

  return (
    <>
      <mesh
        name={`${bounds.componentID}_base_chunk`}
        layers={RenderLayerID.VisibleCollidable}
        geometry={geometry}
        material={
          Array.isArray(material) ? importedMaterials : importedMaterials?.[0]
        }
        userData={userData}>
        {!importedMaterials && (
          <meshBasicMaterial
            color={assetColor}
            side={THREE.DoubleSide}
            clippingPlanes={clipConfig.planes}
            polygonOffset={true}
            polygonOffsetFactor={10}
            polygonOffsetUnits={10}
            opacity={currentOpacity}
            transparent
          />
        )}
      </mesh>
    </>
  )
}
